<template>
  <component v-bind="attributes" :is="tag">
    <slot></slot>
    <fs-popper
      v-if="hasBeenAsked"
      v-bind="popperAttrs"
      ref="popper"
      :animation="animation"
      :before-close="beforeClose"
      :default-class="defaultClass"
      :modifiers="modifiers"
      :offset="offset"
      :on-first-update="onFirstUpdate"
      :show="show"
      :placement="placement"
      :class="popperClass"
      :style="popperStyle"
      :tag="popperTag"
      :selector="selector"
      :strategy="strategy"
      :[parentScopeId]="''"
      @mouseenter="onPopperMouseenter"
      @mouseleave="onPopperMouseleave"
    >
      <slot name="popper"></slot>
    </fs-popper>
  </component>
</template>

<script>
import FsPopper from './fs-popper.vue';

function mergeEvents(self, originalEvent, newEvent) {
  return function () {
    originalEvent.apply(this, arguments);
    newEvent.apply(self, arguments);
  };
}

export default {
  name: 'FsPopperEventWrapper',
  inheritAttrs: false,
  props: {
    animation: {
      // Built in animations: none, fade, slide-fade
      type: String,
      default: 'none',
    },
    beforeClose: {
      type: Function,
      default: () => {},
    },
    defaultClass: {
      type: String,
      default: 'popper',
    },
    modifiers: {
      type: Array,
    },
    offset: {
      // Modifier quick-option - see Popper docs here https://popper.js.org/docs/v2/modifiers/offset/
      type: [Array, Function],
    },
    onFirstUpdate: {
      type: Function,
    },
    show: {
      type: Boolean,
      default: false,
    },
    placement: {
      type: String,
      // Placements taken straight from: https://popper.js.org/docs/v2/constructors/#options
      validator: (placement) =>
        [
          'auto',
          'auto-start',
          'auto-end',
          'top',
          'top-start',
          'top-end',
          'bottom',
          'bottom-start',
          'bottom-end',
          'right',
          'right-start',
          'right-end',
          'left',
          'left-start',
          'left-end',
        ].includes(placement),
      default: 'bottom',
    },
    popperAttrs: {
      Object,
    },
    popperClass: {
      type: [Array, Object, String],
    },
    popperStyle: {
      type: [Array, Object, String],
    },
    popperTag: {
      type: String,
      default: 'div',
    },
    selector: {
      type: String,
    },
    // See Popper docs for options: https://popper.js.org/docs/v2/constructors/#options
    strategy: {
      type: String,
      // Strategies taken straight from: https://popper.js.org/docs/v2/constructors/#options
      validator: (strategy) => ['absolute', 'fixed'].includes(strategy),
      default: 'absolute',
    },
    tag: {
      type: String,
      default: 'div',
    },
    trigger: {
      type: String,
      default: 'click',
    },
    isDelayed: {
      type: Boolean,
      default: false,
    },
    /**
     * Asked whether this popper may open, at the moment something tries to. For a condition
     * that changes often and would otherwise have to be read while rendering — a table tells
     * its cells' tooltips to stay down during a drag, and reading that flag in a render would
     * re-render every cell twice per drag.
     */
    canOpen: {
      type: Function,
      default: null,
    },
  },
  components: {
    FsPopper,
  },

  data() {
    return {
      openTimeout: null,
      closeTimeout: null,
      // The popper is built the first time something asks for it. A page renders these by
      // the hundred - every tooltip is one - and a popper that has never been opened has
      // nothing on screen to show for the instance, the teleport and the re-render it costs.
      hasBeenAsked: false,
    };
  },
  computed: {
    attributes() {
      // Loosely inspired by v-tooltip: https://github.com/Akryum/v-tooltip/blob/master/src/components/Popover.vue#L485
      const attributes = { ...this.$attrs };

      this.trigger.split(' ').forEach((e) => {
        if (e === 'click') {
          attributes['onClick'] = attributes['onClick']
            ? mergeEvents(this, attributes['onClick'], this.toggle)
            : this.toggle;
        }

        if (e === 'focus') {
          attributes['onFocus'] = attributes['onFocus'] ? mergeEvents(this, attributes['focus'], this.open) : this.open;
          attributes['onBlur'] = attributes['onBlur'] ? mergeEvents(this, attributes['blur'], this.close) : this.close;
        }

        if (e === 'hover') {
          attributes['onMouseenter'] = attributes['onMouseenter']
            ? mergeEvents(this, attributes['onMouseenter'], this.open)
            : this.open;
          attributes['onMouseleave'] = attributes['onMouseleave']
            ? mergeEvents(this, attributes['onMouseleave'], this.close)
            : this.close;
        }
      });

      return attributes;
    },
    // Workaround for passing scopeId to div.popper (it't not technically the root, but to allow for parent to style with scoped styles - as is expected - it makes sense to add it)
    parentScopeId() {
      return this.$parent.$options.__scopeId || null;
    },
    delayInMs() {
      return this.isDelayed ? 600 : 0;
    },
  },
  methods: {
    async open(event) {
      if (this.canOpen && !this.canOpen()) return;

      clearTimeout(this.closeTimeout);
      if (!this.hasBeenAsked) {
        this.hasBeenAsked = true;
        await this.$nextTick();
      }
      this.openTimeout = setTimeout(() => {
        this.$refs.popper?.open(event);
      }, this.delayInMs);
    },
    close(event) {
      clearTimeout(this.openTimeout);
      // Add small delay for hover to allow moving to tooltip without flickering
      if (this.trigger.includes('hover')) {
        this.closeTimeout = setTimeout(() => {
          this.$refs.popper?.close({ type: 'popper-wrapper-event', event });
        }, 50);
      } else {
        this.$refs.popper?.close({ type: 'popper-wrapper-event', event });
      }
    },
    async toggle(event) {
      if (!this.$refs.popper?.isOpen && this.canOpen && !this.canOpen()) return;

      if (!this.hasBeenAsked) {
        this.hasBeenAsked = true;
        await this.$nextTick();
      }
      this.$refs.popper?.toggle(event);
    },
    onPopperMouseenter() {
      if (this.trigger.includes('hover')) {
        clearTimeout(this.closeTimeout);
      }
    },
    onPopperMouseleave(event) {
      if (this.trigger.includes('hover')) {
        this.close(event);
      }
    },
  },
};
</script>
